import { defineStore } from 'pinia';
import axios from 'axios';

// Homepage Builder admin store. Mirrors useBannerStore's conventions.
// Sections are returned already ordered by the backend (HomepageSection::
// ordered()). `updateSection`'s payload can be a FormData (when an editor is
// uploading logo_file/avatar_file alongside settings) or a plain object —
// axios/Laravel both handle either transparently on this POST route.
export const useHomepageSectionStore = defineStore('homepageSections', {
    state: () => ({
        sections: [],
        loading: false,
        saving: false,
    }),

    actions: {
        async fetchSections() {
            this.loading = true;
            try {
                const { data } = await axios.get('/admin/homepage-sections');
                this.sections = (data.data ?? []).slice().sort((a, b) => a.sort_order - b.sort_order);
            } finally {
                this.loading = false;
            }
        },

        async createSection(payload) {
            this.saving = true;
            try {
                const { data } = await axios.post('/admin/homepage-sections', payload);
                this.sections.push(data.data);
                this.sections.sort((a, b) => a.sort_order - b.sort_order);
                return data.data;
            } finally {
                this.saving = false;
            }
        },

        async updateSection(id, payload) {
            this.saving = true;
            try {
                const { data } = await axios.post(`/admin/homepage-sections/${id}`, payload);
                const idx = this.sections.findIndex((s) => s.id === id);
                if (idx !== -1) this.sections[idx] = data.data;
                return data.data;
            } finally {
                this.saving = false;
            }
        },

        async deleteSection(id) {
            await axios.delete(`/admin/homepage-sections/${id}`);
            this.sections = this.sections.filter((s) => s.id !== id);
        },

        async toggleActive(id) {
            const { data } = await axios.patch(`/admin/homepage-sections/${id}/toggle-active`);
            const idx = this.sections.findIndex((s) => s.id === id);
            if (idx !== -1) this.sections[idx] = data.data;
            return data.data;
        },

        async reorder(orderedIds) {
            await axios.patch('/admin/homepage-sections/reorder', { order: orderedIds });
        },

        // Admin recovery action: recreate any default section that's currently
        // missing (by key), without touching anything that already exists —
        // never overwrites a teacher's customization.
        async restoreDefaults() {
            const { data } = await axios.post('/admin/homepage-sections/restore-defaults');
            this.sections = data.data ?? [];
        },
    },
});
